"use client";

import { useSyncExternalStore } from "react";

const POINTER_FINE_MQ = "(pointer: fine)";

function subscribeMediaQuery(query: string, cb: () => void): () => void {
	const mq = window.matchMedia(query);
	mq.addEventListener("change", cb);
	return () => mq.removeEventListener("change", cb);
}

function subscribePointerFine(cb: () => void): () => void {
	return subscribeMediaQuery(POINTER_FINE_MQ, cb);
}

/** True when the primary pointer is fine (mouse / most trackpads), not coarse touch-first. */
export function usePointerFine(): boolean {
	return useSyncExternalStore(
		subscribePointerFine,
		() => window.matchMedia(POINTER_FINE_MQ).matches,
		() => false,
	);
}

const DESKTOP_LIKE_POINTER_MQ = "(hover: hover) and (pointer: fine)";

function subscribeDesktopLikePointer(cb: () => void): () => void {
	return subscribeMediaQuery(DESKTOP_LIKE_POINTER_MQ, cb);
}

/**
 * True for hover-capable, fine-pointer primary input (typical mouse desktop / laptop).
 * Touch-first phones and many tablets do not match; avoids relying on viewport width alone.
 */
export function useDesktopLikePointerInput(): boolean {
	return useSyncExternalStore(
		subscribeDesktopLikePointer,
		() => window.matchMedia(DESKTOP_LIKE_POINTER_MQ).matches,
		() => false,
	);
}

function subscribeNever(_cb: () => void): () => void {
	return () => {};
}

function readNavigatorReportsMobile(): boolean {
	if (typeof navigator === "undefined") {
		return false;
	}
	const userAgentData = (
		navigator as Navigator & {
			userAgentData?: { mobile?: boolean };
		}
	).userAgentData;
	return userAgentData?.mobile === true;
}

/**
 * True when Client Hints `navigator.userAgentData.mobile` is explicitly true.
 * Unsupported browsers (e.g. Firefox, older Safari) return false; pair with
 * {@link useDesktopLikePointerInput} for capability-based detection.
 */
export function useNavigatorReportsMobileDevice(): boolean {
	return useSyncExternalStore(
		subscribeNever,
		readNavigatorReportsMobile,
		() => false,
	);
}

/**
 * Eligible environment for custom cursor UX: navbar/header preference UI and platform cursor
 * chrome (`PlatformCursors`: smooth cursor, message portal, `cursor-none-override`). Requires
 * hover-capable fine pointer and, when available, Client Hints must not report a mobile class device.
 */
export function useShowCursorStyleControls(): boolean {
	const desktopLike = useDesktopLikePointerInput();
	const navigatorMobile = useNavigatorReportsMobileDevice();
	return desktopLike && !navigatorMobile;
}
